import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  Typography,
  Box,
  CircularProgress,
  SxProps,
  Theme
} from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import { Button } from 'system/atoms';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { TimePicker } from '@mui/x-date-pickers/TimePicker';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { addHours, setHours, setMinutes } from 'date-fns';
import { launchModalsSx } from 'modules/campaign-creation/styles';

const scheduleErrorIconSx: SxProps<Theme> = {
  fontSize: 18,
  color: 'error.main',
  mr: 0.5
};

const scheduleLoadingContentSx: SxProps<Theme> = {
  display: 'flex',
  alignItems: 'center',
  gap: 1
};

// Dynamic style for date label - shows error color when date is in the past
const getScheduleDateLabelSx = (hasError: boolean): SxProps<Theme> => ({
  ...launchModalsSx.scheduleFieldLabel,
  ...(hasError && { color: 'error.main' })
});

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (scheduledDateTime: string) => Promise<boolean>;
  onSuccess?: () => void;
};

export const ScheduleLaunchModal: React.FC<Props> = ({
  isOpen,
  onClose,
  onConfirm,
  onSuccess
}) => {
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Reset to 1 hour from now each time the modal opens
  useEffect(() => {
    if (isOpen) {
      const defaultTime = addHours(new Date(), 1);
      setSelectedDate(defaultTime);
      setSelectedTime(defaultTime);
    }
  }, [isOpen]);

  const handleConfirm = async () => {
    if (!selectedDate || !selectedTime) return;

    // Combine date and time
    const combinedDateTime = setMinutes(
      setHours(selectedDate, selectedTime.getHours()),
      selectedTime.getMinutes()
    );

    setIsLoading(true);
    try {
      const success = await onConfirm(combinedDateTime.toISOString());

      if (success) {
        if (onSuccess) {
          onSuccess();
        } else {
          onClose();
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    if (isLoading) return;
    onClose();
  };

  const isPastDateTime = () => {
    if (!selectedDate || !selectedTime) return false;
    const combinedDateTime = setMinutes(
      setHours(selectedDate, selectedTime.getHours()),
      selectedTime.getMinutes()
    );
    return combinedDateTime < new Date();
  };

  const isConfirmDisabled =
    !selectedDate || !selectedTime || isPastDateTime() || isLoading;

  return (
    <Dialog open={isOpen} onClose={handleClose}>
      <DialogContent sx={launchModalsSx.modalContent}>
        <Typography sx={launchModalsSx.scheduleModalTitle}>
          Schedule launch
        </Typography>

        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={launchModalsSx.scheduleInput}>
            <Typography sx={launchModalsSx.scheduleFieldLabel}>
              Choose time
            </Typography>
            <Typography sx={launchModalsSx.scheduleFieldSubLabel}>
              HH:MM
            </Typography>
            <TimePicker
              value={selectedTime}
              onChange={(newValue) => setSelectedTime(newValue)}
              ampm={false}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true
                }
              }}
            />
          </Box>

          <Box sx={launchModalsSx.scheduleInput}>
            <Typography sx={getScheduleDateLabelSx(isPastDateTime())}>
              Choose date
            </Typography>
            <Typography sx={launchModalsSx.scheduleFieldSubLabel}>
              DD/MM/YYYY
            </Typography>
            {isPastDateTime() && (
              <Box sx={launchModalsSx.errorMessageContainer}>
                <ErrorOutlineIcon sx={scheduleErrorIconSx} />
                <Typography color="error" sx={launchModalsSx.errorMessageText}>
                  Choose a date in the future.
                </Typography>
              </Box>
            )}
            <DatePicker
              value={selectedDate}
              onChange={(newValue) => setSelectedDate(newValue)}
              disablePast
              format="dd/MM/yyyy"
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                  error: isPastDateTime()
                }
              }}
            />
          </Box>
        </LocalizationProvider>

        <Box sx={launchModalsSx.scheduleButtonGroup}>
          <Button
            variant="primary"
            sx={launchModalsSx.primaryButton}
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            {isLoading ? (
              <Box sx={scheduleLoadingContentSx}>
                <CircularProgress size={16} color="inherit" />
                Scheduling...
              </Box>
            ) : (
              'Confirm'
            )}
          </Button>
          <Button
            variant="tertiary"
            sx={launchModalsSx.textButton}
            onClick={handleClose}
            disabled={isLoading}
          >
            Cancel
          </Button>
        </Box>
      </DialogContent>
    </Dialog>
  );
};
